/* ============================================================
   DoughRise — GA4 ecommerce events

   Fires the four ecommerce events the 90-day roadmap's week 4/8/12
   measurement gates depend on:

     view_item       product page view, and product cards scrolled into view
     add_to_cart     fired on the theme's 'cart:added' success event
     begin_checkout  any checkout button click
     purchase        handled natively by Shopify's GA4 channel, NOT here
                     (duplicating it would double-count revenue)

   Plus the DoughRise funnel helpers (P8-01, docs/GA4-SETUP.md):
   drGa4CalcUsed, drGa4Signup, drGa4PlanEmailed, drGa4PlannerUsed and the
   delegated dr_pro_cta_click listener.

   GA4 itself is installed by Shopify's native Google channel (G-V79EN6XGWB);
   this file only pushes events onto the existing dataLayer. It never loads
   or configures a tag of its own.

   Events are pushed with the gtag/dataLayer pattern below, deduped per
   page view or per session as each event needs.
   ============================================================ */
(function () {
  'use strict';

  var CURRENCY = (window.DR_CURRENCY || 'GBP');

  function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }

  function push(name, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      gtag('event', name, params);
    } catch (e) {
      /* analytics must never break the page */
    }
  }

  /* Page-lifetime dedupe, for events that should fire once per page view
     (an item list is a fresh impression every time the page is loaded). */
  var firedThisPage = {};
  function seenThisPage(key) {
    if (firedThisPage[key]) return true;
    firedThisPage[key] = true;
    return false;
  }

  /* Session-scoped dedupe, for events that must not refire on reload or a
     bfcache restore of the same item. */
  function seen(key) {
    var store;
    try {
      store = window.sessionStorage;
    } catch (e) {
      return false;
    }
    if (!store) return false;
    try {
      if (store.getItem(key)) return true;
      store.setItem(key, '1');
    } catch (e) {}
    return false;
  }

  function money(value) {
    var n = parseFloat(value);
    return isNaN(n) ? 0 : Math.round(n * 100) / 100;
  }

  /* ---------------------------------------------------------
     1. view_item — product page
     Reads the JSON blob rendered by the product template.
     --------------------------------------------------------- */
  function productPageView() {
    var el = document.querySelector('[data-dr-ga4-product]');
    if (!el) return;

    var data;
    try {
      data = JSON.parse(el.textContent || '{}');
    } catch (e) {
      return;
    }
    if (!data.id) return;
    if (seen('dr_vi_' + data.id)) return;

    push('view_item', {
      currency: data.currency || CURRENCY,
      value: money(data.price),
      items: [{
        item_id: String(data.id),
        item_name: data.name,
        item_brand: 'DoughRise',
        item_category: data.category || undefined,
        price: money(data.price),
        quantity: 1
      }]
    });
  }

  /* ---------------------------------------------------------
     2. view_item_list / select_item — product cards
     Cards opt in with [data-dr-product-card]. The list event fires
     once per page when the first card becomes visible; select_item
     fires on click.
     --------------------------------------------------------- */
  function cardItems(cards) {
    return Array.prototype.map.call(cards, function (card) {
      return {
        item_id: String(card.getAttribute('data-dr-item-id') || ''),
        item_name: card.getAttribute('data-dr-item-name') || '',
        item_brand: 'DoughRise',
        price: money(card.getAttribute('data-dr-item-price')),
        index: parseInt(card.getAttribute('data-dr-item-index'), 10) || undefined,
        quantity: 1
      };
    });
  }

  function productCards() {
    var cards = document.querySelectorAll('[data-dr-product-card]');
    if (!cards.length) return;

    var listName = (cards[0].closest('[data-wetheme-section-type]') || {}).getAttribute
      ? cards[0].closest('[data-wetheme-section-type]').getAttribute('data-wetheme-section-type')
      : 'product_list';

    /* view_item_list once the list is actually on screen. Deduped per page
       view, not per session: revisiting the page is a genuine new impression. */
    var fired = false;
    function fire() {
      if (fired) return;
      fired = true;
      if (seenThisPage('vil_' + listName)) return;
      push('view_item_list', {
        item_list_id: listName,
        item_list_name: listName,
        items: cardItems(cards)
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            fire();
            io.disconnect();
          }
        });
      }, { threshold: 0.25 });
      io.observe(cards[0]);
    } else {
      fire();
    }

    /* select_item on click. */
    Array.prototype.forEach.call(cards, function (card) {
      card.addEventListener('click', function () {
        push('select_item', {
          item_list_id: listName,
          item_list_name: listName,
          items: cardItems([card])
        });
      });
    });
  }

  /* ---------------------------------------------------------
     3. add_to_cart — read from the /cart/add response itself.

     Earlier attempts keyed off the theme's 'cart:added' eventBus event and
     then tried to confirm the add by diffing the cart's item_count. That is
     unreliable in both directions: 'cart:added' is emitted unconditionally
     in component-product-form.js processResponse (after the 422 branch), it
     carries only a sectionId so it cannot identify which of a section's many
     quick-add forms was used, and a global count moves for reasons unrelated
     to this add (the customer editing quantities in the cart drawer, gift
     wrap being added alongside the product, concurrent requests).

     The /cart/add response is the authoritative per-request answer: it is
     scoped to exactly one add, tells us whether it succeeded (HTTP ok vs
     422), and returns the line item(s) actually created, with the real
     quantity, variant id, title and price. The theme performs every add with
     fetch, so wrapping fetch observes all of them: product forms, quick-add
     buttons, cross-sell and complementary products.
     --------------------------------------------------------- */
  function addToCart() {
    if (typeof window.fetch !== 'function') return;

    var nativeFetch = window.fetch;

    function isCartAdd(url) {
      if (!url) return false;
      var s = String(url);
      return s.indexOf('/cart/add') !== -1;
    }

    /* Shopify returns either a single line item or { items: [...] }. */
    function linesFrom(payload) {
      if (!payload) return [];
      if (Array.isArray(payload.items)) return payload.items;
      if (payload.id || payload.variant_id) return [payload];
      return [];
    }

    function reportLines(lines) {
      var items = [];
      var total = 0;

      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        if (!line) continue;
        /* final_line_price / line_price are in minor units for the whole line. */
        var linePrice = line.final_line_price;
        if (typeof linePrice !== 'number') linePrice = line.line_price;
        var unit = line.final_price;
        if (typeof unit !== 'number') unit = line.price;

        var qty = typeof line.quantity === 'number' ? line.quantity : 1;
        var unitMajor = typeof unit === 'number' ? unit / 100 : undefined;
        if (typeof linePrice === 'number') total += linePrice / 100;

        items.push({
          item_id: String(line.variant_id || line.id || ''),
          item_name: line.product_title || line.title || undefined,
          item_variant: line.variant_title || undefined,
          item_brand: 'DoughRise',
          price: unitMajor !== undefined ? money(unitMajor) : undefined,
          quantity: qty
        });
      }

      if (!items.length) return;

      push('add_to_cart', {
        currency: CURRENCY,
        value: money(total),
        items: items
      });
    }

    /* Adds still unreported when the page goes away.

       A ticket is held from the moment the request is wrapped and is only
       discarded once the event has actually been pushed, so a body that
       resolves after the 500ms cap (while the caller is already navigating)
       is still flushed here. Anything whose body never arrived is genuinely
       unknowable and is dropped rather than guessed at.

       pagehide is dispatched on window, not document. visibilitychange is
       registered too because iOS Safari can background a page without ever
       firing pagehide. */
    var inFlight = [];

    function holdForUnload(parse, fallback) {
      var ticket = { lines: null, fallback: fallback || null, reported: false };
      parse.then(function (lines) { ticket.lines = lines; }, function () {});
      inFlight.push(ticket);
      return ticket;
    }

    /* What the REQUEST said, used only if the page is torn down before the
       response body arrives. Less complete than the server's answer (no
       product title, and the price is unknown), but a reported add with the
       right variant and quantity beats a silently lost conversion. */
    function linesFromRequest(init) {
      if (!init || !init.body) return null;
      var body = init.body;

      try {
        /* The main product form posts FormData (component-product-form.js
           fetchConfigWithBody), which is the site's primary add path.
           URLSearchParams is handled the same way. */
        if (typeof body.get === 'function' && typeof body.append === 'function') {
          var fid = body.get('id');
          if (!fid) return null;
          var fqty = body.get('quantity');
          return [{
            variant_id: String(fid),
            quantity: parseInt(fqty, 10) || 1
          }];
        }

        if (typeof body === 'string') {
          if (body.charAt(0) === '{') {
            var parsed = JSON.parse(body);
            var items = parsed.items || [parsed];
            var out = [];
            for (var i = 0; i < items.length; i++) {
              if (!items[i] || !items[i].id) continue;
              out.push({
                variant_id: items[i].id,
                quantity: parseInt(items[i].quantity, 10) || 1
              });
            }
            return out.length ? out : null;
          }

          /* urlencoded: id=123&quantity=1 */
          var id = /(?:^|&)id=([^&]*)/.exec(body);
          if (!id) return null;
          var qty = /(?:^|&)quantity=([^&]*)/.exec(body);
          return [{
            variant_id: decodeURIComponent(id[1]),
            quantity: qty ? (parseInt(decodeURIComponent(qty[1]), 10) || 1) : 1
          }];
        }
      } catch (e) {}
      return null;
    }

    /* Called once the event has been pushed through the normal path. */
    function release(ticket) {
      ticket.reported = true;
      var i = inFlight.indexOf(ticket);
      if (i !== -1) inFlight.splice(i, 1);
    }

    /* terminal=true only for pagehide, where the page really is going away.
       Backgrounding via visibilitychange is NOT terminal: the body may still
       arrive after an app switch or a bfcache restore, so an unresolved
       ticket must survive that flush and stay reportable. */
    function flushPending(terminal) {
      if (!inFlight.length) return;
      var keep = [];
      for (var i = 0; i < inFlight.length; i++) {
        var t = inFlight[i];
        if (t.reported) continue;

        if (t.lines && t.lines.length) {
          t.reported = true;
          try { reportLines(t.lines); } catch (e) {}
          continue;
        }

        /* No body yet. While the page lives, keep waiting for the real
           answer. If it is being torn down, the response will never arrive,
           so report what the request asked for rather than lose the event. */
        if (!terminal) {
          keep.push(t);
        } else if (t.fallback && t.fallback.length) {
          t.reported = true;
          try { reportLines(t.fallback); } catch (e) {}
        }
      }
      inFlight = keep;
    }

    window.addEventListener('pagehide', function () { flushPending(true); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flushPending(false);
    });

    window.fetch = function (input, init) {
      var url = (input && input.url) ? input.url : input;
      var result = nativeFetch.apply(this, arguments);
      if (!isCartAdd(url)) return result;

      return result.then(function (response) {
        /* Only a successful add counts. A 422 (sold out, invalid quantity)
           must not be reported. */
        if (!response || !response.ok) return response;

        /* Resolve only once the event has been pushed. Several landing pages
           (lp-bakery-ops, lp-open-crumb, lp-pizza-pro, lp-tracker-pro,
           lp-diagnose-dense-loaf) navigate to /cart the moment this promise
           resolves, which would unload the page before a detached parse could
           report.

           The 500ms cap is deliberate and must not be removed: on a slow
           body it releases the caller so analytics can never delay or block
           a real purchase. To keep that from costing an event, the pending
           add is also registered with flushOnUnload below, so if navigation
           does win the race the event is still sent as the page goes away. */
        var settled = false;
        return new Promise(function (resolve) {
          function done() {
            if (settled) return;
            settled = true;
            resolve(response);
          }

          var timer = setTimeout(done, 500);

          /* clone() throws if another wrapper already consumed or locked the
             body. Analytics must never turn a successful add into a rejected
             fetch, so fail open: release the caller with the real response
             and simply skip the event. */
          var parse;
          try {
            parse = response.clone().json().then(function (payload) {
              return linesFrom(payload);
            }, function () {
              return [];
            });
          } catch (e) {
            clearTimeout(timer);
            done();
            return;
          }

          /* If the page unloads before the body arrives, still send it,
             falling back to what the request asked for. */
          var ticket = holdForUnload(parse, linesFromRequest(init));

          try {
            parse.then(function (lines) {
              /* Report first, then release: releasing early would drop the
                 ticket before the unload flush could ever see it. */
              if (!ticket.reported) {
                try { reportLines(lines); } catch (e) {}
                release(ticket);
              }
              clearTimeout(timer);
              done();
            }, function () {
              release(ticket);
              clearTimeout(timer);
              done();
            });
          } catch (e) {
            release(ticket);
            clearTimeout(timer);
            done();
          }
        });
      });
    };
  }

  /* ---------------------------------------------------------
     4. begin_checkout — checkout buttons
     Covers the cart page, the cart drawer, and any [name="checkout"].
     --------------------------------------------------------- */
  function beginCheckout() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var btn = t.closest('[name="checkout"], [data-dr-checkout], a[href="/checkout"], a[href^="/checkout?"]');
      if (!btn) return;

      var total = document.querySelector('[data-dr-cart-total]');
      push('begin_checkout', {
        currency: CURRENCY,
        value: total ? money(total.getAttribute('data-dr-cart-total')) : undefined
      });
    }, true);
  }

  /* ---------------------------------------------------------
     5. Calculator cross-sell panels.

     cross_sell_view fires once per panel when it actually scrolls into
     view (not on render: most calculator pages are long and the panel sits
     below the results, so a render-time event would overstate impressions).
     cross_sell_click fires on the CTA.

     These feed the roadmap's week 8 gate, which asks whether calculator
     traffic converts at all.
     --------------------------------------------------------- */
  function crossSell() {
    var panels = document.querySelectorAll('[data-dr-crosssell]');
    if (!panels.length) return;

    function payload(panel) {
      return {
        calculator: panel.getAttribute('data-dr-calc') || undefined,
        currency: CURRENCY,
        value: money(panel.getAttribute('data-dr-item-price')),
        items: [{
          item_id: String(panel.getAttribute('data-dr-item-id') || ''),
          item_name: panel.getAttribute('data-dr-item-name') || undefined,
          item_brand: 'DoughRise',
          price: money(panel.getAttribute('data-dr-item-price')),
          quantity: 1
        }]
      };
    }

    Array.prototype.forEach.call(panels, function (panel) {
      /* Sections can re-render in the theme editor and on tab switches;
         bind once per panel. */
      if (panel.getAttribute('data-dr-xs-bound')) return;
      panel.setAttribute('data-dr-xs-bound', '1');

      var calc = panel.getAttribute('data-dr-calc') || 'unknown';

      /* One impression per PANEL per session: switching tabs back and forth
         on the hub page should not inflate the count, but a calculator that
         promotes two products (e.g. bakers -> Formula Vault + cheat sheets)
         must still record an impression for each. Keying on the calculator
         alone would let the first panel suppress the second. */
      var dedupeKey = 'dr_xs_' + calc + '_' + (panel.getAttribute('data-dr-item-id') || '');
      function viewed() {
        if (seen(dedupeKey)) return;
        push('cross_sell_view', payload(panel));
      }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            /* Panels for inactive calculator tabs are display:none, so they
               never intersect. That is the behaviour we want. */
            if (entry.isIntersecting) {
              viewed();
              io.disconnect();
            }
          });
        }, { threshold: 0.5 });
        io.observe(panel);
      } else if (panel.offsetParent !== null) {
        /* No IntersectionObserver: fall back to a visibility check so panels
           belonging to inactive calculator tabs (display:none) are not
           counted as impressions. */
        viewed();
      }

      var cta = panel.querySelector('[data-dr-crosssell-cta]');
      if (cta) {
        cta.addEventListener('click', function () {
          push('cross_sell_click', payload(panel));
        });
      }
    });
  }

  /* Short, safe label for an event parameter: lower case letters, digits,
     '_', '-' and ':' only, at most 40 characters. Strips anything that could
     carry personal data (an email address can never survive it, '@' and '.'
     are both dropped). */
  function label(value, fallback) {
    var s = String(value == null ? '' : value).toLowerCase()
      .replace(/[^a-z0-9_:-]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);
    return s || fallback || 'unknown';
  }

  /* ---------------------------------------------------------
     User-interaction flag (P8-01)
     A calculator result only counts once a real person has touched the
     page. Boot renders, URL pre-fills and share-link renders all happen
     before the first trusted pointer, key or input event, so they can
     never fire dr_calculator_used, whatever the call site does.
     --------------------------------------------------------- */
  var userActed = false;
  /* Only events a person causes. load and DOMContentLoaded are trusted
     too, so isTrusted alone is not enough. */
  var USER_EVENTS = /^(?:click|dblclick|pointer(?:down|up)|mouse(?:down|up)|touch(?:start|end)|key(?:down|up|press)|input|change|submit)$/;
  function markActed(e) {
    if (!e || e.isTrusted !== false) userActed = true;
  }
  ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'input', 'change'].forEach(function (type) {
    try { document.addEventListener(type, markActed, { capture: true, passive: true }); } catch (e) {}
  });
  function isUserDriven() {
    if (userActed) return true;
    /* The script is deferred, so an interaction can beat the listeners.
       Fall back to the event being handled right now. */
    try {
      var ev = window.event;
      if (ev && ev.isTrusted === true && USER_EVENTS.test(ev.type)) { userActed = true; return true; }
    } catch (e) {}
    return false;
  }
  window.drUserActed = isUserDriven;

  /* ---------------------------------------------------------
     6. dr_calculator_used — first successful run of a calculator
     Called from each calculator's existing calc.run site. calc.run
     feeds the internal cockpit (once per session, free users only);
     this is the GA4 side of the same moment, so it fires for every
     visitor, once per calculator per page view, with is_pro attached
     for segmenting. No inputs or personal data in the payload.
     Ignored until the visitor has interacted with the page (see above).
     --------------------------------------------------------- */
  window.drGa4CalcUsed = function (calculator, surface) {
    if (!isUserDriven()) return;
    calculator = label(calculator);
    if (seenThisPage('dr_calculator_used:' + calculator)) return;
    push('dr_calculator_used', {
      calculator: calculator,
      surface: label(surface),
      is_pro: window.__drIsPro === true
    });
  };

  /* ---------------------------------------------------------
     7. dr_newsletter_signup (P8-01) — the one signup event.
     Called after a SUCCESSFUL /api/leads response by every signup form.
     Replaces the footer's old generate_lead, so a signup is counted once
     under one name. `source` is the lead source the form posted, cleaned
     by label(); never the email. Once per page view.
     --------------------------------------------------------- */
  window.drGa4Signup = function (source) {
    if (seenThisPage('dr_newsletter_signup')) return;
    push('dr_newsletter_signup', {
      source: label(source, 'newsletter'),
      is_pro: window.__drIsPro === true
    });
  };

  /* ---------------------------------------------------------
     8. dr_plan_emailed (P8-01) — an "email me this result" send worked.
     Pass the fetch Response from /api/leads when there is one. Production
     answers { ok: true, confirm: 'sent' | 'not-needed' }: 'sent' means this
     request asked a consenting, not yet confirmed address to confirm, i.e. a
     new newsletter signup. Only then does dr_newsletter_signup fire as well.
     The send itself never counts as a signup. Once per calculator per page.
     --------------------------------------------------------- */
  window.drGa4PlanEmailed = function (calculator, response, source) {
    calculator = label(calculator);
    if (!seenThisPage('dr_plan_emailed:' + calculator)) {
      push('dr_plan_emailed', {
        calculator: calculator,
        is_pro: window.__drIsPro === true
      });
    }
    if (!response || typeof response.clone !== 'function') return;
    try {
      response.clone().json().then(function (body) {
        if (body && body.confirm === 'sent') window.drGa4Signup(source || 'calc-result');
      }, function () {});
    } catch (e) {}
  };

  /* ---------------------------------------------------------
     9. dr_planner_used (P8-01) — first user-driven plan in the Saturday
     Dough Planner (sections/dr-planner.liquid), the same moment as its
     drGa4CalcUsed('dough-planner'). Once per page view.
     --------------------------------------------------------- */
  window.drGa4PlannerUsed = function () {
    if (!isUserDriven()) return;
    if (seenThisPage('dr_planner_used')) return;
    push('dr_planner_used', { is_pro: window.__drIsPro === true });
  };

  /* ---------------------------------------------------------
     10. dr_pro_cta_click (P8-01) — clicks towards a paid tier.
     One delegated listener. Counts links to the tier, pricing and founding
     pages, the three subscription products, and anything carrying
     data-dr-cta="<location>" (optionally data-dr-tier="<tier>"). Skipped
     for paying members. Sent with transport_type beacon and never touches
     the click itself, so navigation is not delayed or blocked.
     --------------------------------------------------------- */
  var CTA_PATH = /^\/(?:pages\/(pro|coach|bakery|pricing|founding-members)|products\/doughrise-(pro|coach|bakery))(?:[\/?#]|$)/;
  var CTA_HOSTS = /^(?:www\.)?doughrise\.store$/;

  function ctaPath(el) {
    var href = el.getAttribute && el.getAttribute('href');
    if (!href) return null;
    var path = href;
    if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.indexOf('//') === 0) {
      var m = /^(?:[a-z]+:)?\/\/([^\/?#]+)(.*)$/i.exec(href);
      if (!m) return null;
      var host = m[1].toLowerCase();
      var here = (window.location && window.location.hostname) || '';
      if (host !== here && !CTA_HOSTS.test(host)) return null;
      path = m[2] || '/';
    }
    path = path.replace(/^\/(?:[a-z]{2}(?:-[a-z]{2})?)\/(?=pages\/|products\/)/i, '/');
    return CTA_PATH.exec(path) ? path : null;
  }

  function ctaTier(path) {
    var m = path && CTA_PATH.exec(path);
    if (!m) return 'unknown';
    var t = m[1] || m[2];
    if (t === 'pricing') return 'all';
    if (t === 'founding-members') return 'founding';
    return t;
  }

  function ctaLocation(el) {
    var sec = el.closest && el.closest('[id^="shopify-section-"]');
    if (sec) {
      var id = sec.id.replace(/^shopify-section-/, '');
      var cut = id.lastIndexOf('__');
      return label(cut !== -1 ? id.slice(cut + 2) : id, 'page');
    }
    var p = (window.location && window.location.pathname) || '';
    return label(p.replace(/^\/+/, '').replace(/\//g, '-'), 'home');
  }

  function proCtaClicks() {
    document.addEventListener('click', function (e) {
      if (window.__drIsPro === true) return;
      var t = e.target;
      if (!t || !t.closest) return;
      var el = t.closest('[data-dr-cta]') || t.closest('a[href]');
      if (!el) return;

      var explicit = el.getAttribute('data-dr-cta');
      var link = el.matches && el.matches('a[href]') ? el : (t.closest('a[href]') || el);
      var path = ctaPath(link);
      if (explicit === null && !path) return;

      push('dr_pro_cta_click', {
        location: explicit ? label(explicit) : ctaLocation(el),
        tier: el.getAttribute('data-dr-tier') ? label(el.getAttribute('data-dr-tier')) : ctaTier(path),
        transport_type: 'beacon'
      });
    }, true);
  }

  /* P6-02: a pizza night guest page was made. */
  window.drGa4NightCreated = function (showDough) {
    push('dr_night_created', { show_dough: !!showDough, is_pro: window.__drIsPro === true });
  };

  /* P6-01: a shareable formula card was made (one event per card, not per page). */
  window.drGa4CardCreated = function (calculator) {
    push('dr_card_created', { calculator: String(calculator || 'unknown'), is_pro: window.__drIsPro === true });
  };

  function init() {
    productPageView();
    crossSell();
    productCards();
    addToCart();
    beginCheckout();
    proCtaClicks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
